import {
  Alert,
  Button,
  Card,
  Field,
  Input,
  Skeleton,
  Stack,
  Text,
  Textarea,
} from '@chakra-ui/react';
import { toaster, useColorModeValue } from '@mono/chakra';
import { isHttpError, useHttp } from '@mono/http-hook';
import { FC, useEffect, useMemo, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useCurrentProject } from '../projects/hooks';

type FormValues = {
  name: string;
  slug: string;
  description: string;
};

const getErrorMessage = (error: unknown, fallback: string) => {
  if (isHttpError(error)) {
    const responseData = error.response?.data;
    if (responseData && typeof responseData === 'object') {
      const { error: errorMessage, message: responseMessage } =
        responseData as { error?: string; message?: string };
      return errorMessage ?? responseMessage ?? error.message ?? fallback;
    }
    return error.message ?? fallback;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return fallback;
};

const ProjectSettingsTab: FC = () => {
  const projectQuery = useCurrentProject();
  const http = useHttp();
  const [serverError, setServerError] = useState<string | null>(null);
  const cardBg = useColorModeValue('bg.surface', 'gray.900');

  const { handleSubmit, register, reset, formState } = useForm<FormValues>({
    defaultValues: {
      name: '',
      slug: '',
      description: '',
    },
  });

  const project = projectQuery.data?.project;

  useEffect(() => {
    if (project) {
      reset(
        {
          name: project.name ?? '',
          slug: project.slug ?? '',
          description: project.description ?? '',
        },
        { keepDirty: false }
      );
      setServerError(null);
    }
  }, [project, reset]);

  const canEdit = useMemo(() => {
    if (!project) return false;
    return ['ADMIN', 'MAINTAIN'].includes(project.role);
  }, [project]);

  const roleLabel = useMemo(() => {
    if (!project) return '';
    return project.role.charAt(0) + project.role.slice(1).toLowerCase();
  }, [project]);

  const onSubmit: SubmitHandler<FormValues> = async (values) => {
    if (!project) return;
    setServerError(null);

    const payload = {
      name: values.name.trim(),
      slug: values.slug.trim(),
      description: values.description.trim(),
    };

    try {
      await http.patch(
        `/api/v1/organizations/${project.organizationId}/projects/${project.id}`,
        payload,
        {
          withCredentials: true,
        }
      );

      await projectQuery.refetch();
      const refreshed = projectQuery.data?.project ?? {
        ...project,
        ...payload,
        description: payload.description || '',
      };

      reset(
        {
          name: refreshed.name,
          slug: refreshed.slug,
          description: refreshed.description ?? '',
        },
        { keepDirty: false }
      );

      toaster.create({
        type: 'success',
        title: 'Project updated',
        description: 'Your changes have been saved.',
      });
    } catch (error) {
      const message = getErrorMessage(
        error,
        'Unable to update project right now.'
      );
      setServerError(message);
      toaster.create({
        type: 'error',
        title: 'Failed to update project',
        description: message,
      });
    }
  };

  const handleReset = () => {
    if (!project) return;
    reset(
      {
        name: project.name ?? '',
        slug: project.slug ?? '',
        description: project.description ?? '',
      },
      { keepDirty: false }
    );
    setServerError(null);
  };

  if (projectQuery.isLoading) {
    return (
      <Stack gap="6">
        <Skeleton height="20" borderRadius="lg" />
        <Skeleton height="20" borderRadius="lg" />
      </Stack>
    );
  }

  if (projectQuery.error || !project) {
    return (
      <Alert.Root status="error">
        <Alert.Indicator />
        <Alert.Content>
          <Alert.Title>Unable to load project settings</Alert.Title>
          <Alert.Description>
            Please refresh the page or try again later.
          </Alert.Description>
        </Alert.Content>
      </Alert.Root>
    );
  }

  return (
    <Stack gap="6">
      {!canEdit ? (
        <Alert.Root status="info">
          <Alert.Indicator />
          <Alert.Content>
            <Alert.Title>Read-only access</Alert.Title>
            <Alert.Description>
              You have {roleLabel.toLowerCase()} permissions on this project.
              Only maintainers or admins can update settings.
            </Alert.Description>
          </Alert.Content>
        </Alert.Root>
      ) : null}

      <Card.Root bg={cardBg}>
        <Card.Header gap="1">
          <Card.Title>Project details</Card.Title>
          <Card.Description>
            Update the name, slug, and description that teammates see.
          </Card.Description>
        </Card.Header>
        <Card.Body>
          <form id="project-settings-form" onSubmit={handleSubmit(onSubmit)}>
            <Stack gap="5">
              <Field.Root invalid={Boolean(formState.errors.name)}>
                <Field.Label>Project name</Field.Label>
                <Input
                  placeholder="Customer Retention Initiative"
                  {...register('name', {
                    required: 'Project name is required.',
                    minLength: {
                      value: 2,
                      message: 'Name must contain at least 2 characters.',
                    },
                  })}
                  disabled={!canEdit || formState.isSubmitting}
                />
                <Field.HelperText>
                  Choose a clear, action-oriented name for your project.
                </Field.HelperText>
                <Field.ErrorText>{formState.errors.name?.message}</Field.ErrorText>
              </Field.Root>

              <Field.Root invalid={Boolean(formState.errors.slug)}>
                <Field.Label>Slug</Field.Label>
                <Input
                  placeholder="customer-retention"
                  {...register('slug', {
                    pattern: {
                      value: /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
                      message:
                        'Use lowercase letters, numbers, and hyphens only.',
                    },
                  })}
                  disabled={!canEdit || formState.isSubmitting}
                />
                <Field.HelperText>
                  Used in URLs. Leave blank to derive from the project name.
                </Field.HelperText>
                <Field.ErrorText>{formState.errors.slug?.message}</Field.ErrorText>
              </Field.Root>

              <Field.Root invalid={Boolean(formState.errors.description)}>
                <Field.Label>Description</Field.Label>
                <Textarea
                  placeholder="Outline the goals, deliverables, and success measures."
                  rows={5}
                  {...register('description', {
                    maxLength: {
                      value: 500,
                      message: 'Description must be 500 characters or less.',
                    },
                  })}
                  disabled={!canEdit || formState.isSubmitting}
                />
                <Field.HelperText>
                  Provide context so collaborators understand the vision.
                </Field.HelperText>
                <Field.ErrorText>
                  {formState.errors.description?.message}
                </Field.ErrorText>
              </Field.Root>

              {serverError ? (
                <Text color="fg.error" fontSize="sm">
                  {serverError}
                </Text>
              ) : null}
            </Stack>
          </form>
        </Card.Body>
        <Card.Footer gap="3" justifyContent="flex-end">
          <Button
            variant="ghost"
            onClick={handleReset}
            disabled={
              !formState.isDirty || formState.isSubmitting || !canEdit
            }
          >
            Reset
          </Button>
          <Button
            type="submit"
            form="project-settings-form"
            loading={formState.isSubmitting}
            disabled={!formState.isDirty || !canEdit}
            colorPalette="primary"
          >
            Save changes
          </Button>
        </Card.Footer>
      </Card.Root>

      <Card.Root bg={cardBg}>
        <Card.Header gap="1">
          <Card.Title>Project metadata</Card.Title>
          <Card.Description>
            Snapshot of key details for reference.
          </Card.Description>
        </Card.Header>
        <Card.Body gap="3">
          <Stack gap="1">
            <Text fontWeight="medium">Project ID</Text>
            <Text color="fg.muted" fontSize="sm">
              {project.id}
            </Text>
          </Stack>
          <Stack gap="1">
            <Text fontWeight="medium">Current role</Text>
            <Text color="fg.muted" fontSize="sm">
              {roleLabel}
            </Text>
          </Stack>
          <Stack gap="1">
            <Text fontWeight="medium">Last updated</Text>
            <Text color="fg.muted" fontSize="sm">
              {new Date(project.updatedAt).toLocaleString()}
            </Text>
          </Stack>
        </Card.Body>
      </Card.Root>
    </Stack>
  );
};

export default ProjectSettingsTab;
